import { useEffect, useState } from 'react';
import SectionComparison from 'core/common/components/QuizScreensSections/SectionComparison';
import SectionFeaturedIn from 'core/common/components/QuizScreensSections/SectionFeaturedIn';
import SectionIntroOffer from 'core/common/components/QuizScreensSections/SectionIntroOffer';
import SectionPayment from 'core/common/components/QuizScreensSections/SectionPayment';
import SectionPaywallFooter from 'core/common/components/QuizScreensSections/SectionPaywallFooter';
import SectionPaywallHero from 'core/common/components/QuizScreensSections/SectionPaywallHero';
import SectionRating from 'core/common/components/QuizScreensSections/SectionRating';
import SectionReviewsCarousel from 'core/common/components/QuizScreensSections/SectionReviewsCarousel';
import SectionWhatYouGet from 'core/common/components/QuizScreensSections/SectionWhatYouGet';
import RenderOnlyOnClient from 'core/common/components/RenderOnlyOnClient';
import { ReviewsCardAvatar } from 'core/common/components/ReviewsCards';
import { SECOND } from 'core/common/constants';
import { scrollToBlock } from 'core/common/utils/scroll';
import {
  SectionType,
  SectionAttributesUnionType,
  TrialPaymentWithDiscountPalmistryV3ScreenProps,
} from 'core/funnel/entities';
import { useObservers } from './useObservers';
import {
  ContainerStyled,
  EmptyBlockStyled,
  SectionFaqV2Styled,
  SectionGuaranteeStyled,
  SectionDeckSliderStyled,
  IntroOfferWrapperStyled,
  DeckSliderWrapperStyled,
  SectionPalmAnalysisStyled,
  SectionPaywallHeaderStyled,
  SectionPlansOverviewStyled,
  ReviewsCarouselWrapperStyled,
  SectionSubscriptionDiscountStyled,
} from './styled';

const firstOfferBlockId = 'intro-offer';

const TrialPaymentWithDiscountPalmistryV3Screen = ({
  offer,
  sections,
  timeLeft,
  isTimeUp,
  timeUpOffers,
  defaultOffers,
  selectedOffer,
  goNext,
  getReport,
  onOfferChanged,
}: TrialPaymentWithDiscountPalmistryV3ScreenProps) => {
  const {
    setOffersRef,
    topPageRef,
    firstOfferRef,
    secondOfferRef,
    deckSliderRef,
    reviewCarouselRef,
  } = useObservers();

  const [shouldBlurDeckSlider, setShouldBlurDeckSlider] = useState(false);
  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);

  const shouldDisplayHeader = !topPageRef.inView && !firstOfferRef.inView && !secondOfferRef.inView;

  const scrollToOffer = () => {
    scrollToBlock(firstOfferBlockId);
  };

  const onGetReport = () => {
    setDisplayPaymentModal(true);
    getReport();
  };

  const onPaymentModalClose = () => {
    setDisplayPaymentModal(false);
  };

  useEffect(() => {
    let timeoutId = null;

    if (deckSliderRef.inView) {
      setShouldBlurDeckSlider(true);

      timeoutId = setTimeout(() => {
        setShouldBlurDeckSlider(false);
      }, 4 * SECOND);
    }

    return () => {
      if (!timeoutId) return;

      clearTimeout(timeoutId);
    };
  }, [deckSliderRef.inView]);

  if (!offer) {
    return null;
  }

  const renderSection = (section: SectionAttributesUnionType, index: number) => {
    switch (section.sectionType) {
      case SectionType.PAYWALL_HEADER:
        return (
          <>
            <EmptyBlockStyled ref={topPageRef.intersection} />
            <SectionPaywallHeaderStyled
              key={section.sectionType + index}
              content={section.content}
              timeLeft={timeLeft}
              isTimeUp={isTimeUp}
              onGetPlan={scrollToOffer}
              isVisible={shouldDisplayHeader}
            />
          </>
        );
      case SectionType.PAYWALL_HERO:
        return <SectionPaywallHero key={section.sectionType + index} content={section.content} />;
      case SectionType.SUBSCRIPTION_DISCOUNT:
        return (
          <SectionSubscriptionDiscountStyled
            key={section.sectionType + index}
            content={section.content}
            onClick={scrollToOffer}
          />
        );
      case SectionType.DECK_SLIDER:
        return (
          <DeckSliderWrapperStyled
            ref={deckSliderRef.intersection}
            key={section.sectionType + index}
          >
            <SectionDeckSliderStyled content={section.content} blurred={shouldBlurDeckSlider} />
          </DeckSliderWrapperStyled>
        );
      case SectionType.COMPARISON:
        return <SectionComparison key={section.sectionType + index} content={section.content} />;
      case SectionType.PLANS_OVERVIEW:
        return (
          <SectionPlansOverviewStyled
            content={section.content}
            key={section.sectionType + index}
            onGetPlan={scrollToOffer}
          />
        );
      case SectionType.INTRO_OFFER:
        return (
          <IntroOfferWrapperStyled
            ref={setOffersRef}
            id={firstOfferBlockId}
            key={section.sectionType + index}
          >
            <SectionIntroOffer
              chosenPlan={offer}
              isTimeUp={isTimeUp}
              content={section.content}
              timeUpOffers={timeUpOffers}
              selectedPlan={selectedOffer}
              defaultOffers={defaultOffers}
              onGetReport={onGetReport}
              onPlanChanged={onOfferChanged}
            />
          </IntroOfferWrapperStyled>
        );
      case SectionType.WHAT_YOU_GET:
        return <SectionWhatYouGet key={section.sectionType + index} content={section.content} />;
      case SectionType.PALM_ANALYSIS:
        return (
          <SectionPalmAnalysisStyled key={section.sectionType + index} content={section.content} />
        );
      case SectionType.FEATURED_IN:
        return <SectionFeaturedIn key={section.sectionType + index} content={section.content} />;
      case SectionType.REVIEWS_CAROUSEL:
        return (
          <ReviewsCarouselWrapperStyled
            key={section.sectionType + index}
            ref={reviewCarouselRef.intersection}
          >
            <SectionReviewsCarousel
              content={section.content}
              shouldPlayAnimation={reviewCarouselRef.inView}
              renderReviewCard={({ date, review, userName, userAvatar }) => {
                if (!userAvatar) {
                  return null;
                }

                return (
                  <ReviewsCardAvatar
                    date={date}
                    review={review}
                    userName={userName}
                    userAvatar={userAvatar}
                  />
                );
              }}
            />
          </ReviewsCarouselWrapperStyled>
        );
      case SectionType.RATING:
        return <SectionRating key={section.sectionType + index} content={section.content} />;
      case SectionType.FAQ_V2:
        return <SectionFaqV2Styled key={section.sectionType + index} content={section.content} />;
      case SectionType.GUARANTEE:
        return (
          <SectionGuaranteeStyled key={section.sectionType + index} content={section.content} />
        );
      case SectionType.PAYWALL_FOOTER:
        return <SectionPaywallFooter key={section.sectionType + index} content={section.content} />;
      case SectionType.PAYMENT_V2:
        return (
          !!offer && (
            <RenderOnlyOnClient key={section.sectionType + index}>
              <SectionPayment
                offer={offer}
                isTimeUp={isTimeUp}
                isOpen={displayPaymentModal}
                content={section.content}
                onPaymentSuccess={goNext}
                onClose={onPaymentModalClose}
              />
            </RenderOnlyOnClient>
          )
        );
      default:
        return null;
    }
  };

  return (
    <ContainerStyled>{sections.map((item, index) => renderSection(item, index))}</ContainerStyled>
  );
};

export default TrialPaymentWithDiscountPalmistryV3Screen;
